import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  FIELD_CLASS,
  Spinner,
  Table,
  Td,
  Th,
  cx,
} from '@/components/ui';
import { MoneyInput } from '@/components/MoneyInput';
import { useConfirm } from '@/components/ConfirmProvider';
import {
  formatDateTime,
  formatKrwPlain,
  formatPercent,
  formatUsdPricePlain,
} from '@/lib/format';
import { isUsMarket, resolveQuote, type QuoteView } from './priceDisplay';
import { QuoteCell, StockIdentity } from './components';
import { TradingNav } from './TradingNav';
import { StockDetailModal } from './StockDetailModal';
import { marketScopeLabel, useMarketScope, type MarketScope } from './marketScope';
import type { Price, PriceAlert } from './stockTypes';
import { usePriceAlertMutations, usePriceAlerts, usePrices } from './useStocks';

/**
 * 가격 알림 현황. (2026-08-23 사용자 요청 · 2026-09-02 종목별 묶음)
 *
 * 🔴 종목으로 묶는다 (2026-09-02)
 *   한 종목에 최대 5개를 걸 수 있게 되면서(V123) 평면 목록이 무너졌다.
 *   삼성전자 3개와 SK하이닉스 4개가 걸린 화면에서 시간순으로 섞여 나오면
 *   "이 종목에 지금 뭐가 걸려 있나" 를 눈으로 모을 수가 없다.
 *
 *   묶음 안에서는 기준가가 높은 순이다. 현재가를 가운데 두고 위아래로 읽힌다 -
 *   위쪽은 팔 자리(목표), 아래쪽은 살 자리(저점)다.
 *
 *   묶음의 순서는 현재가가 기준가에 가장 가까운 종목이 먼저다.
 *   곧 울릴 것 같은 종목이 위에 있어야 화면을 열었을 때 바로 보인다.
 *
 * 실시간 가격은 관심종목과 같은 훅(usePrices)이다 - 15초마다 갱신된다.
 *
 * 🔴 위의 국내주식/미국주식 스위치를 따른다 (2026-09-28 사용자 결정, I-83 ①)
 *   전에는 공통 화면이라 두 나라 알림이 한 목록에 섞였다. 관심종목과 같은 방식으로 거른다 -
 *   알림 표는 하나고 화면에서만 가른다(상한 · 발동 검사는 그대로).
 *
 * 🔴 종목 순서는 화면에 들어올 때 한 번 정하고 지킨다 (2026-09-28 사용자 결정 A안, I-83 ③)
 *   되돌리기 · 삭제 뒤 "맨 위로 튄다" 는 피드백의 원인이 순서였다[추론 - 코드 흐름].
 *   울린 알림은 기준가 근처에서 꺼진 것이라, 다시 걸면 그 종목이 가장 가까운 종목이 되어
 *   목록 맨 위로 올라갔다. 15초 시세 갱신만으로도 종목 순서가 저절로 바뀌었다.
 */
export function AlertsPage() {
  const alerts = usePriceAlerts();
  const { removeOne, rearm, changePrice, rearmMany, removeMany } = usePriceAlertMutations();
  const scope = useMarketScope((s) => s.scope);
  const confirm = useConfirm();
  /** 일괄 처리 결과 한 줄(실패가 있었을 때만). 다음 일괄 처리 때 지운다 */
  const [bulkNote, setBulkNote] = useState<string | null>(null);

  /*
    종목을 누르면 상세 모달이 열린다. (2026-09-03 사용자 요청)
    거기서 알림을 바로 더 걸 수 있다 - 전에는 관심종목 화면으로 돌아가
    같은 종목을 다시 찾아야 했다.
  */
  const [openStockId, setOpenStockId] = useState<number | null>(null);

  const scoped = filterByScope(alerts.data ?? [], scope);
  const stockIds = [...new Set(scoped.map((alert) => alert.stockId))];
  // 마지막 알림을 지워 종목 묶음이 바뀌어도 직전 시세를 들고 있는다 - 현재가가 '-' 로 깜빡이지 않게
  const prices = usePrices(stockIds, { keepPrevious: true });

  const live = groupAlerts(scoped, prices.data ?? []);

  /*
    순서 고정. 나라를 바꾸면 새로 정한다.

    🔴 알림과 "그 종목들의" 시세가 둘 다 온 뒤에 정한다
      시세가 없으면 거리를 못 재서 전부 이름순이다. 그 순서로 굳히면
      "곧 울릴 종목이 위" 가 영영 안 된다. 두 가지 함정이 있다 -
      ① 알림을 불러오기 전에는 목록이 비어 있어 "종목 0개" 로 먼저 굳고, 알림이 오면
         전부 뒤에 붙는다(이름순). 그래서 알림이 온 뒤에만 정한다
      ② 나라를 바꾼 직후 시세 자리에는 앞 나라 시세(keepPrevious)가 들어 있다.
         그것은 이 종목들의 값이 아니라 isPlaceholderData 이면 기다린다
      시세 요청이 실패하면 굳히지 않고 지금 순서(이름순)를 그대로 보여 준다.

    렌더 중에 상태를 고치는 것은 React 가 허용하는 "이전 렌더에서 값 맞추기" 방식이다 -
    바뀔 때만 set 하므로 되돌이가 멈춘다.
  */
  const [frozen, setFrozen] = useState<{ scope: MarketScope; ids: number[] } | null>(null);
  const current = frozen?.scope === scope ? frozen.ids : null;
  const ready =
    alerts.data !== undefined &&
    (stockIds.length === 0 || (prices.data !== undefined && !prices.isPlaceholderData));
  const liveIds = live.map((group) => group.stockId);
  const next = ready ? freezeOrder(current, liveIds) : current;
  if (next !== null && (current === null || !sameIds(current, next))) {
    setFrozen({ scope, ids: next });
  }
  const groups = next === null ? live : applyOrder(live, next);

  /*
    울린 알림 한꺼번에 다시 걸기 · 삭제 (2026-10-09 사용자 요청)
    대상은 지금 화면의 나라 것만이다(triggeredAlerts). 둘 다 확인창을 거친다 —
    삭제는 되돌릴 수 없고, 다시 걸기는 이미 지난 기준가가 곧바로 또 울릴 수 있다.
  */
  const triggered = triggeredAlerts(groups);
  const busy =
    removeOne.isPending ||
    rearm.isPending ||
    changePrice.isPending ||
    rearmMany.isPending ||
    removeMany.isPending;

  async function rearmAllTriggered() {
    const ok = await confirm({
      title: `${marketScopeLabel(scope)} 울린 알림 ${triggered.ids.length}건을 모두 다시 걸까요?`,
      description:
        triggered.refire > 0
          ? `기준가는 그대로 두고 다시 켭니다. 이 중 ${triggered.refire}건은 지금 가격이 이미 기준가를 지나 있어 다음 검사(15초 안)에 바로 다시 울립니다.`
          : '기준가는 그대로 두고 다시 켭니다.',
      confirmText: '모두 다시 걸기',
    });
    if (!ok) {
      return;
    }
    setBulkNote(null);
    const failed = await rearmMany.mutateAsync({ alertIds: triggered.ids });
    if (failed > 0) {
      setBulkNote(`${failed}건은 다시 걸지 못했습니다. 잠시 뒤 다시 눌러 주세요.`);
    }
  }

  async function removeAllTriggered() {
    const ok = await confirm({
      title: `${marketScopeLabel(scope)} 울린 알림 ${triggered.ids.length}건을 모두 지울까요?`,
      description: '지운 알림은 되돌릴 수 없습니다. 켜져 있는 알림은 그대로 둡니다.',
      confirmText: '모두 삭제',
      tone: 'danger',
    });
    if (!ok) {
      return;
    }
    setBulkNote(null);
    const failed = await removeMany.mutateAsync({ alertIds: triggered.ids });
    if (failed > 0) {
      setBulkNote(`${failed}건은 지우지 못했습니다. 잠시 뒤 다시 눌러 주세요.`);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <TradingNav />

      <header>
        <h1 className="text-xl font-semibold">가격 알림</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {/*
            ✏️ 2026-09-28 I-78 2차 — 두 조건이 빠져 있었다
              ① Slack 은 관리자만 받는다(I-82 · PushSubscriptionService). 다른 회원은 폰 알림뿐이다
              ② 시세가 15분 넘게 멈춰 있으면(시간외 무체결) 울리지 않는다(PriceAlertService.QUOTE_FRESH_MINUTES) —
                 마감가로 거짓 알림이 나가는 것을 막는 장치다. 9/20 1차 대조에서 남긴 미해결이었다
          */}
          걸어둔 기준가에 닿으면 <strong>폰 알림</strong>으로 알려드립니다(관리자는 Slack 도).
          정규장과 시간외를 포함해 그 종목이 거래되는 시간 내내 15초마다 검사하며, 한 번 울리면
          꺼집니다. 다만 시간외에 15분 넘게 체결이 없어 시세가 멈춰 있으면 울리지 않습니다. 한 종목에
          5개까지 걸 수 있고, 기준가를 누르면 그 자리에서 고칠 수 있습니다(고치면 다시 켜집니다).
          울린 알림은 목록 위의 버튼으로 한꺼번에 다시 걸거나 지울 수 있습니다.
        </p>
      </header>

      {alerts.isLoading && <Spinner label="알림을 불러오는 중" />}
      {alerts.error && (
        <ErrorState message={alerts.error.message} onRetry={() => void alerts.refetch()} />
      )}

      {alerts.data && scoped.length === 0 && (
        <Card>
          {/*
            다른 나라에만 알림이 있으면 그렇다고 말한다 (I-83 ①)
            "없습니다" 만 보이면 알림이 지워진 것으로 읽힌다.
          */}
          <EmptyState
            title={`${marketScopeLabel(scope)}에 걸어둔 알림이 없습니다`}
            description={
              alerts.data.length > 0
                ? `${marketScopeLabel(scope === 'US' ? 'KR' : 'US')}에 ${alerts.data.length}건이 걸려 있습니다. 위의 스위치로 바꿔 보세요.`
                : '관심종목에서 종목을 열고 기준가를 입력하면 여기에 모입니다.'
            }
          />
        </Card>
      )}

      {groups.length > 0 && (
        <>
          <p className="text-xs text-fg-subtle">
            {marketScopeLabel(scope)} · 종목 {groups.length}개 · 알림 {scoped.length}건
          </p>

          {triggered.ids.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="text-fg-muted">울린 알림 {triggered.ids.length}건</span>
              <button
                type="button"
                className="font-medium text-brand underline hover:opacity-80 disabled:opacity-50"
                onClick={() => void rearmAllTriggered()}
                disabled={busy}
              >
                모두 다시 걸기
              </button>
              <button
                type="button"
                className="text-fg-muted underline hover:text-fg disabled:opacity-50"
                onClick={() => void removeAllTriggered()}
                disabled={busy}
              >
                모두 삭제
              </button>
              {bulkNote !== null && <span className="w-full text-danger">{bulkNote}</span>}
            </div>
          )}

          {groups.map((group) => (
            <AlertGroupCard
              key={group.stockId}
              group={group}
              onRemove={(alertId) => removeOne.mutate({ alertId })}
              onRearm={(alertId) => rearm.mutate({ alertId })}
              onChangePrice={(alertId, targetPrice) =>
                changePrice.mutateAsync({ alertId, targetPrice }).then(() => undefined)
              }
              onOpen={() => setOpenStockId(group.stockId)}
              busy={busy}
            />
          ))}

          <p className="text-xs text-fg-subtle">
            {/*
              🔴 "장이 닫혀 있으면" 을 빼먹고 있었다 (2026-09-20)
                다른 화면은 전부 이 단서를 붙인다(주도주·섹터·저점매수).
              2026-09-28 — 나라별로 가르면서 "국내·미국이 섞인다" 단서는 뺐다(I-83 ①).
            */}
            현재가는 장이 열려 있는 동안 15초마다 갱신되고, 닫혀 있으면 마지막 종가입니다.
            종목 순서는 이 화면을 열 때 곧 울릴 종목부터 정하고, 보는 동안에는 바뀌지
            않습니다. 종목 이름을 누르면 차트를 보고 알림을 더 걸 수 있습니다. 폰 알림은
            내 정보에서 켤 수 있습니다.
          </p>
        </>
      )}

      <StockDetailModal stockId={openStockId} onClose={() => setOpenStockId(null)} />
    </div>
  );
}

/** 한 종목과 그 종목에 걸린 알림들. */
export interface AlertGroup {
  stockId: number;
  stockName: string;
  symbol: string | null;
  market: string | null;
  /** 현재가·대비·등락률. 시세를 못 받았으면 값이 null 이다 */
  view: QuoteView;
  /** 기준가 내림차순 */
  alerts: PriceAlert[];
  /** 가장 가까운 알림까지의 거리(%). 묶음 순서를 정한다. 못 재면 null */
  nearest: number | null;
}

/**
 * 알림을 종목으로 묶는다.
 *
 * 화면과 갈라 둔 이유는 정렬 규칙이 두 겹이라 눈으로는 검증이 안 되기 때문이다.
 * (묶음 사이의 순서 · 묶음 안의 순서)
 *
 * @param alerts 서버가 준 알림 전체. 종목이 섞여 있다
 * @param prices 같은 종목들의 현재가
 */
export function groupAlerts(alerts: PriceAlert[], prices: Price[]): AlertGroup[] {
  const byStock = new Map<number, PriceAlert[]>();
  for (const alert of alerts) {
    const list = byStock.get(alert.stockId);
    if (list) {
      list.push(alert);
    } else {
      byStock.set(alert.stockId, [alert]);
    }
  }

  const groups: AlertGroup[] = [];
  for (const [stockId, list] of byStock) {
    const view = resolveQuote(
      prices.find((p) => p.stockId === stockId),
      null,
      null,
    );
    const sorted = [...list].sort((a, b) => b.targetPrice - a.targetPrice);
    // Map 에 들어온 이상 최소 한 건이다. 종목 이름·거래소는 어느 행에서 읽어도 같다
    const head = sorted[0];
    if (head === undefined) {
      continue;
    }

    groups.push({
      stockId,
      stockName: head.stockName ?? `종목 ${stockId}`,
      symbol: head.symbol,
      market: head.market,
      view,
      alerts: sorted,
      nearest: nearestGap(sorted, view.price),
    });
  }

  /*
    곧 울릴 것 같은 종목을 위로.
    거리를 못 재는 종목(시세 없음·전부 울림)은 맨 뒤로 보내되 목록에서 빼지는 않는다 -
    걸어둔 알림이 화면에서 사라지면 "지워졌나" 로 읽힌다.
  */
  return groups.sort((a, b) => {
    if (a.nearest === null && b.nearest === null) {
      return a.stockName.localeCompare(b.stockName, 'ko-KR');
    }
    if (a.nearest === null) return 1;
    if (b.nearest === null) return -1;
    return a.nearest - b.nearest;
  });
}

/**
 * 아직 안 울린 알림 중 현재가에서 가장 가까운 것까지의 거리(%).
 *
 * 🔴 꺼진 알림은 세지 않는다 - 이미 울린 것이라 다시 안 울린다.
 *   전부 꺼져 있으면 null 이고, 그 종목은 목록 뒤로 간다.
 */
function nearestGap(alerts: PriceAlert[], current: number | null): number | null {
  if (current === null || current === 0) {
    return null;
  }
  const gaps = alerts
    .filter((alert) => alert.active)
    .map((alert) => Math.abs((alert.targetPrice - current) / current) * 100);
  return gaps.length === 0 ? null : Math.min(...gaps);
}

/**
 * 남은 거리 - 현재가가 기준가에서 몇 % 떨어져 있나. 현재가를 모르면 null.
 *
 * 부호를 방향에 맞춰 뒤집는다: 저점 알림은 "얼마나 더 내려야 하나",
 * 목표 알림은 "얼마나 더 올라야 하나" 다. 둘 다 양수면 아직 멀었다는 뜻이고,
 * 0 이하면 이미 지났다는 뜻이라 켜져 있으면 다음 검사에서 울린다.
 * (✏️ 2026-10-09 알림 줄 안에 있던 계산을 꺼냈다 — 일괄 다시 걸기도 같은 잣대를 쓴다)
 */
export function alertGap(alert: PriceAlert, price: number | null): number | null {
  if (price === null) {
    return null;
  }
  return (
    ((alert.direction === 'BELOW' ? price - alert.targetPrice : alert.targetPrice - price) /
      alert.targetPrice) *
    100
  );
}

/**
 * 울려서 꺼진 알림 — 일괄 다시 걸기 · 삭제의 대상. (2026-10-09 사용자 요청)
 *
 * 화면에 보이는 묶음(지금 고른 나라)만 본다. 다른 나라 알림까지 건드리면 안 보이는 것이 바뀐다.
 * refire 는 다시 걸면 곧바로 또 울릴 것의 수다 — 서버의 다시 걸기는 지금 가격을 보지 않고
 * 스위치만 올리므로, 이미 기준가를 지난 알림은 다음 검사에서 다시 울린다. 확인창에서 미리 알린다.
 * 현재가를 모르는 알림은 refire 로 세지 않는다(모르는 것을 "울린다" 고 하지 않는다).
 */
export function triggeredAlerts(groups: AlertGroup[]): { ids: number[]; refire: number } {
  const ids: number[] = [];
  let refire = 0;
  for (const group of groups) {
    for (const alert of group.alerts) {
      if (alert.active) {
        continue;
      }
      ids.push(alert.id);
      const gap = alertGap(alert, group.view.price);
      if (gap !== null && gap <= 0) {
        refire += 1;
      }
    }
  }
  return { ids, refire };
}

/**
 * 스위치에서 고른 나라의 알림만 남긴다. (2026-09-28, I-83 ①)
 *
 * 나라는 알림에 실린 거래소(market)로 가른다 - 관심종목과 같은 isUsMarket 이다.
 * 종목을 못 찾아 거래소가 null 인 알림은 국내로 본다(화면에서 사라지지 않게).
 */
export function filterByScope(alerts: PriceAlert[], scope: MarketScope): PriceAlert[] {
  return alerts.filter((alert) => isUsMarket(alert.market) === (scope === 'US'));
}

/**
 * 화면에 들어올 때 정한 종목 순서를 지킨다. (2026-09-28 사용자 결정 A안, I-83 ③)
 *
 * - 처음(prev 가 null)이면 지금 순서(곧 울릴 종목부터)를 그대로 굳힌다
 * - 그 뒤에는 순서를 바꾸지 않는다. 다시 걸기 · 삭제 · 15초 시세 갱신으로 거리가 바뀌어도
 *   종목이 자리를 옮기지 않는다 - 누른 줄이 눈앞에서 사라지지 않게
 * - 사라진 종목(마지막 알림을 지움)은 빼고, 새로 생긴 종목은 맨 뒤에 붙인다
 * - 화면을 나갔다 오거나 나라를 바꾸면 다시 정한다(호출부가 prev 를 null 로 준다)
 *
 * @param prev 지금까지 지켜 온 순서(종목 id). 처음이면 null
 * @param live 지금 규칙(groupAlerts)으로 세운 순서
 */
export function freezeOrder(prev: number[] | null, live: number[]): number[] {
  if (prev === null) {
    return live;
  }
  const alive = new Set(live);
  const kept = prev.filter((id) => alive.has(id));
  const known = new Set(kept);
  return [...kept, ...live.filter((id) => !known.has(id))];
}

/** 두 순서가 같은가. 같으면 상태를 다시 쓰지 않는다(렌더가 되돌지 않게) */
export function sameIds(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/**
 * 굳힌 순서대로 묶음을 세운다. 순서에 없는 종목은 지금 순서대로 뒤에 붙인다 -
 * 시세가 오기 전 잠깐 동안 새 종목이 목록에서 빠지지 않게.
 */
export function applyOrder(groups: AlertGroup[], order: number[]): AlertGroup[] {
  const rank = new Map(order.map((id, i) => [id, i]));
  const ordered = groups
    .filter((group) => rank.has(group.stockId))
    .sort((a, b) => (rank.get(a.stockId) ?? 0) - (rank.get(b.stockId) ?? 0));
  return [...ordered, ...groups.filter((group) => !rank.has(group.stockId))];
}

function AlertGroupCard({
  group,
  onRemove,
  onRearm,
  onChangePrice,
  onOpen,
  busy,
}: {
  group: AlertGroup;
  onRemove: (alertId: number) => void;
  onRearm: (alertId: number) => void;
  /** 기준가를 고친다. 실패하면 reject 된다 - 줄이 입력칸을 닫지 않고 사유를 보여 준다 */
  onChangePrice: (alertId: number, targetPrice: number) => Promise<void>;
  /** 종목 상세 모달을 연다. 거기서 알림을 더 걸 수 있다 */
  onOpen: () => void;
  busy: boolean;
}) {
  const us = isUsMarket(group.market);
  const money = (value: number | null) =>
    value === null ? '-' : us ? formatUsdPricePlain(value) : formatKrwPlain(value);

  return (
    <Card>
      {/*
        종목 머리 - 이름·거래소·현재가. 알림이 몇 개든 한 번만 나온다.

        🔴 머리만 버튼이다. 표 전체를 버튼으로 감싸지 않는다 (2026-09-03)
          안에 삭제·되돌리기 버튼이 있어서, 감싸면 그것을 누를 때마다
          모달까지 함께 열린다.
      */}
      <button
        type="button"
        onClick={onOpen}
        className="mb-2 -mx-2 flex w-[calc(100%+1rem)] flex-wrap items-start justify-between gap-2 rounded-lg px-2 py-1 text-left hover:bg-surface-muted"
      >
        <span className="min-w-0">
          <span className="block font-medium text-fg">{group.stockName}</span>
          <StockIdentity market={group.view.market ?? group.market} symbol={group.symbol} />
        </span>
        <span className="shrink-0 text-right">
          <QuoteCell view={group.view} />
        </span>
      </button>

      <Table>
        <thead>
          <tr>
            <Th>조건</Th>
            <Th align="right">기준가</Th>
            <Th align="right">남은 거리</Th>
            <Th>{''}</Th>
          </tr>
        </thead>
        <tbody>
          {group.alerts.map((alert) => (
            <AlertRow
              key={alert.id}
              alert={alert}
              price={group.view.price}
              money={money}
              us={us}
              busy={busy}
              onRemove={() => onRemove(alert.id)}
              onRearm={() => onRearm(alert.id)}
              onChangePrice={(targetPrice) => onChangePrice(alert.id, targetPrice)}
            />
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function AlertRow({
  alert,
  price,
  money,
  us,
  busy,
  onRemove,
  onRearm,
  onChangePrice,
}: {
  alert: PriceAlert;
  price: number | null;
  money: (value: number | null) => string;
  /** 미국 종목이면 센트(소수)를 받는 칸을 쓴다 */
  us: boolean;
  busy: boolean;
  onRemove: () => void;
  /** 울려서 꺼진 알림을 다시 켠다 */
  onRearm: () => void;
  /** 기준가를 고친다. 실패하면 reject 된다 */
  onChangePrice: (targetPrice: number) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <PriceEditRow
        alert={alert}
        us={us}
        onSave={onChangePrice}
        onClose={() => setEditing(false)}
      />
    );
  }

  const gap = alertGap(alert, price);

  return (
    <tr className={cx(!alert.active && 'opacity-60')}>
      <Td>
        <Badge tone={alert.direction === 'BELOW' ? 'info' : 'success'}>
          {alert.directionLabel}
        </Badge>
        {!alert.active && (
          <span className="ml-1 whitespace-nowrap text-xs text-fg-subtle">
            {alert.triggeredAt ? `${formatDateTime(alert.triggeredAt)} 울림` : '꺼짐'}
          </span>
        )}
      </Td>
      <Td align="right" numeric>
        {/*
          기준가를 누르면 그 자리에서 고친다 (2026-09-28 사용자 요청, I-83 ②)
          점선 밑줄은 "누를 수 있다" 표시다. 삭제 · 다시 걸기의 실선 밑줄과 구분한다.
        */}
        <button
          type="button"
          className="tabular underline decoration-dotted underline-offset-4 hover:text-brand disabled:opacity-50"
          onClick={() => setEditing(true)}
          disabled={busy}
          aria-label={`${money(alert.targetPrice)} 기준가 고치기`}
        >
          {money(alert.targetPrice)}
        </button>
      </Td>
      <Td align="right" numeric>
        {gap === null ? (
          <span className="text-fg-subtle">-</span>
        ) : (
          /*
            🔴 상승·하락 색을 쓰지 않는다
              여기 숫자는 등락이 아니라 "기준까지 남은 거리" 다.
              0 이하면 이미 지나 다음 검사에서 울린다는 뜻이라 진하게,
              아직 멀었으면 흐리게 둔다. 빨강·파랑을 쓰면 등락률로 읽힌다.
          */
          <span className={gap <= 0 ? 'font-medium text-fg' : 'text-fg-muted'}>
            {formatPercent(gap)}
          </span>
        )}
      </Td>
      <Td align="right">
        {/*
          🔴 울려서 꺼진 알림에는 [다시 걸기] 를 준다 (2026-09-03 사용자 요청)
            전에는 삭제뿐이라, 같은 자리에 다시 걸려면 종목 모달을 열어
            기준가를 손으로 옮겨 적어야 했다. 기준가는 그대로 두고
            스위치만 올린다 - 다른 값으로 걸려면 기준가를 눌러 고친다(2026-09-28).
        */}
        <span className="inline-flex items-center gap-2 whitespace-nowrap">
          {!alert.active && (
            <button
              type="button"
              className="text-xs font-medium text-brand underline hover:opacity-80 disabled:opacity-50"
              onClick={onRearm}
              disabled={busy}
              aria-label={`${money(alert.targetPrice)} 알림 다시 걸기`}
            >
              다시 걸기
            </button>
          )}
          <button
            type="button"
            className="text-xs text-fg-muted underline hover:text-fg disabled:opacity-50"
            onClick={onRemove}
            disabled={busy}
            aria-label={`${money(alert.targetPrice)} 알림 삭제`}
          >
            삭제
          </button>
        </span>
      </Td>
    </tr>
  );
}

/**
 * 기준가를 고치는 줄. (2026-09-28 사용자 요청, I-83 ②)
 *
 * 고치는 동안은 줄 전체를 입력 줄로 바꾼다 - 폰 폭에서 기준가 칸 하나에
 * 입력칸과 버튼 둘을 넣으면 표가 가로로 밀린다.
 *
 * Enter 로 저장, Esc 로 취소. 실패하면 칸을 닫지 않고 서버가 준 사유를 보여 준다.
 * 방향(저점/목표)은 바꿀 수 없다 - 바꾸려면 지우고 종목 모달에서 새로 건다.
 */
function PriceEditRow({
  alert,
  us,
  onSave,
  onClose,
}: {
  alert: PriceAlert;
  us: boolean;
  onSave: (targetPrice: number) => Promise<void>;
  onClose: () => void;
}) {
  const [input, setInput] = useState(String(alert.targetPrice));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 열리자마자 입력칸에 커서를 둔다 - 폰에서 칸을 한 번 더 누르지 않게.
  // MoneyInput 이 autoFocus 를 넘기지 않아(공용 컴포넌트) 폼 안의 첫 입력칸을 찾는다
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    formRef.current?.querySelector('input')?.select();
  }, []);

  const parsed = Number(input.replaceAll(',', ''));
  const valid = Number.isFinite(parsed) && parsed > 0;

  const submit = () => {
    if (!valid || saving) return;
    // 값도 그대로고 켜져 있으면 보낼 것이 없다. 꺼진 알림이면 같은 값이라도 보낸다 - 다시 켜진다
    if (parsed === alert.targetPrice && alert.active) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    onSave(parsed)
      .then(onClose)
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : '기준가를 고치지 못했습니다.');
        setSaving(false);
      });
  };

  return (
    <tr>
      {/* 공용 Td 는 colSpan 을 받지 않는다(공유 영역이라 넓히지 않았다) - 같은 선 · 여백을 직접 준다 */}
      <td colSpan={4} className="border-b border-line py-2">
        <form
          ref={formRef}
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
          }}
        >
          <Badge tone={alert.direction === 'BELOW' ? 'info' : 'success'}>
            {alert.directionLabel}
          </Badge>
          {us ? (
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              inputMode="decimal"
              autoComplete="off"
              aria-label="새 기준가 ($)"
              className={cx(FIELD_CLASS, 'w-32 text-right tabular')}
            />
          ) : (
            <MoneyInput
              value={input}
              onChange={setInput}
              aria-label="새 기준가 (원)"
              className="w-36"
            />
          )}
          <button
            type="submit"
            className="text-xs font-medium text-brand underline hover:opacity-80 disabled:opacity-50"
            disabled={!valid || saving}
          >
            {saving ? '저장 중' : '저장'}
          </button>
          <button
            type="button"
            className="text-xs text-fg-muted underline hover:text-fg"
            onClick={onClose}
          >
            취소
          </button>
          {error !== null && <span className="w-full text-xs text-danger">{error}</span>}
        </form>
      </td>
    </tr>
  );
}
